import React, { useEffect, useRef, useState } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  getAgent,
  getAgentChats,
  getAgentSupportingContent,
  getAgentMedia,
  saveAgent,
  deleteChatSession,
  deleteSupportingContent,
  generateSupportingContent,
} from "actions/agents";
import { getTrainingWorkflows, getInferenceWorkflows } from "actions/workflows";
import Input from "components/Elements/Input";
import ErrorField from "components/ErrorField";
import Select from "react-select";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import MediaItem from "components/Media/MediaItem";

// components
import Sidebar from "components/Sidebar/Sidebar.js";
import CardFullTable from "components/Cards/Admin/CardFullTable.js";
// texts as props
import sidebar from "_texts/admin/sidebar/sidebar.js";

const Agent = () => {
  const dispatch = useDispatch();
  const { agent_id } = useParams();
  const {
    agent,
    agentChatSessions,
    agentMedia,
    agentSupportingContent,
    saving,
  } = useSelector((state) => state.agents);
  const { activeUser } = useSelector((state) => state.users);
  const { trainingWorkflows, inferenceWorkflows } = useSelector(
    (state) => state.workflows
  );
  const [selectedTrainingWorkflow, setSelectedTrainingWorkflow] = useState(
    null
  );
  const [selectedInferenceWorkflow, setSelectedInferenceWorkflow] = useState(
    null
  );

  const [trainingWorkflowOptions, setTrainingWorkflowOptions] = useState([]);
  const [inferenceWorkflowOptions, setInferenceWorkflowOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localAgent, setLocalAgent] = useState(null);
  const [media, setMedia] = useState([]);
  const [tokenCount, setTokenCount] = useState(null);
  const [chatSessions, setChatSessions] = useState(null);
  const [loadingChatSessions, setLoadingChatSessions] = useState(true);
  const [loadingSupportingContent, setLoadingSupportingContent] = useState(
    true
  );
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [supportingContent, setSupportingContent] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();
  const toast = useRef();
  const chatSessionsHead = [
    "id",
    "public_id",
    "user",
    "message",
    "count",
    "status",
    "updated",
    "created",
    "actions",
  ];

  const supportingContentHead = [
    "id",
    "public_id",
    "name",
    "description",
    "workflow",
    "",
  ];

  useEffect(() => {
    /*if (agent_id && (agent === null || localAgent === null)) {
      setLoading(true);
      dispatch(getAgent(agent_id));
    } else if (!agent_id) {
      setLoading(false);
      setLocalAgent(null);
    }*/
    dispatch(getInferenceWorkflows());
    dispatch(getTrainingWorkflows());

    return () => {
      setLocalAgent(null);
      setLoading(true);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (chatSessions) {
      setLoadingChatSessions(false);
    }
  }, [chatSessions]);

  useEffect(() => {
    if (media) {
      setLoadingMedia(false);
    }
  }, [media]);

  useEffect(() => {
    if (supportingContent) {
      setLoadingSupportingContent(false);
    }
  }, [supportingContent]);

  useEffect(() => {
    if (!agentChatSessions) return;

    console.log(
      "!!! SETTING CHAT SESSIONS: ",
      agentChatSessions,
      typeof agentChatSessions
    );
    setChatSessions(
      agentChatSessions
        ?.sort((a, b) => (a.id < b.id ? 1 : -1))
        .map((c) => {
          const status_colors = {
            "not reviewed": "#bbbbbb",
            approved: "#008800",
            rejected: "#880000",
          };
          let status = (
            <div
              style={{
                padding: "4px",
                backgroundColor: status_colors[c.status ?? "not reviewed"],
                fontWeight: "bold",
                color: "white",
              }}
            >
              {c.status ?? "not reviewed"}
            </div>
          );
          const values = [
            c.id,
            c.public_id ?? "",
            c.user ?? "",
            c.user_message ?? "",
            c.message_count ?? 0,
            status,
            c.updated_at ?? "",
            c.created_at ?? "",
            <>
              {activeUser && activeUser.user_type === "admin" && (
                <div
                  className="bg-red-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-3 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDialog({
                      message: `Are you sure you want to delete session ${c.public_id}?`,
                      header: "Delete Chat Session",
                      icon: "pi pi-exclamation-triangle",
                      accept: () => {
                        dispatch(
                          deleteChatSession(c.public_id, () => {
                            toast.current.show({
                              severity: "info",
                              summary: "Deleted",
                              detail: "Chat Session Deleted",
                              life: 3000,
                            });
                          })
                        );
                      },
                    });
                  }}
                >
                  delete
                </div>
              )}
            </>,
          ];
          return values;
        })
    );
    // eslint-disable-next-line
  }, [agentChatSessions]);

  useEffect(() => {
    if (!agentSupportingContent) return;

    console.log(
      "!!! SETTING SUPPORTING CONTENT: ",
      agentSupportingContent,
      typeof agentSupportingContent
    );
    setSupportingContent(
      agentSupportingContent
        .sort((a, b) => (a.id < b.id ? 1 : -1))
        .map((c) => {
          const values = [
            c.id,
            c.public_id ?? "",
            c.name ?? "",
            c.description ?? "",
            c.workflow_name ?? "",
            <>
              {activeUser && activeUser.user_type === "admin" && (
                <>
                  <div
                    className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-3 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDialog({
                        message: `Are you sure you want to generate supporting content for ${c.name}?`,
                        header: "Regenerating Supporting Content",
                        icon: "pi pi-exclamation-triangle",
                        accept: () => {
                          dispatch(
                            generateSupportingContent(c.public_id, () => {
                              toast.current.show({
                                severity: "info",
                                summary: "Generated",
                                detail: "Generated Supporing Content",
                                life: 3000,
                              });
                            })
                          );
                        },
                      });
                    }}
                  >
                    generate
                  </div>
                  <div
                    className="bg-red-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-3 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDialog({
                        message: `Are you sure you want to delete supported content ${c.name}?`,
                        header: "Delete Supporting Content",
                        icon: "pi pi-exclamation-triangle",
                        accept: () => {
                          dispatch(
                            deleteSupportingContent(c.public_id, () => {
                              toast.current.show({
                                severity: "info",
                                summary: "Deleted",
                                detail: "Supporting Content Deleted",
                                life: 3000,
                              });
                            })
                          );
                        },
                      });
                    }}
                  >
                    delete
                  </div>
                </>
              )}
            </>,
          ];
          return values;
        })
    );
    // eslint-disable-next-line
  }, [agentSupportingContent]);

  useEffect(() => {
    console.log("!!! CHECKGIN AGENT MEDIA: ", agentMedia);
    if (!agentMedia) return;

    let count = 0;
    agentMedia.forEach((m) => {
      count += m.token_count;
    });
    console.log("!!! TOKEN COUNT: ", count);
    setTokenCount(count);

    console.log("!!! SETTING MEDIA: ", agentMedia, typeof agentMedia);
    setMedia(agentMedia);
  }, [agentMedia]);

  useEffect(() => {
    if (agent_id && localAgent?.public_id !== agent_id) {
      setLoading(true);
      console.log("!!! GET AGENT: ", agent_id);
      dispatch(getAgent(agent_id));
      dispatch(getAgentChats(agent_id));
      dispatch(getAgentSupportingContent(agent_id));
      dispatch(getAgentMedia(agent_id));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [agent_id]);

  useEffect(() => {
    if (trainingWorkflows && trainingWorkflowOptions.length === 0) {
      console.log(
        "TRAINING WORKFlOW OPTIONS: ",
        trainingWorkflows.map((w) => {
          return { value: w.public_id, label: w.name };
        })
      );
      setTrainingWorkflowOptions(
        trainingWorkflows.map((w) => {
          return { value: w.public_id, label: w.name };
        })
      );
    }

    if (inferenceWorkflows && inferenceWorkflowOptions.length === 0) {
      setInferenceWorkflowOptions(
        inferenceWorkflows.map((w) => {
          return { value: w.public_id, label: w.name };
        })
      );
    }
    // eslint-disable-next-line
  }, [trainingWorkflows, inferenceWorkflows]);

  useEffect(() => {
    if (agent && trainingWorkflowOptions && inferenceWorkflowOptions) {
      const trainingWorkflow = trainingWorkflowOptions.find(
        (w) => w.value === agent.training_workflow_id
      );
      const inferenceWorkflow = inferenceWorkflowOptions.find(
        (w) => w.value === agent.inference_workflow_id
      );

      setSelectedTrainingWorkflow(trainingWorkflow);
      setSelectedInferenceWorkflow(inferenceWorkflow);
    }
  }, [trainingWorkflowOptions, inferenceWorkflowOptions, agent]);

  useEffect(() => {
    if (agent) {
      console.log("SETTING CHAT SESSIONS: ", agentChatSessions);

      const trainingWorkflow = trainingWorkflowOptions.find(
        (w) => w.value === agent.training_workflow_id
      );
      const inferenceWorkflow = inferenceWorkflowOptions.find(
        (w) => w.value === agent.inference_workflow_id
      );

      setSelectedTrainingWorkflow(trainingWorkflow);
      setSelectedInferenceWorkflow(inferenceWorkflow);

      /*if (!localAgent.public_id) {
        window.history.replaceState(
          null,
          "Edit Agent",
          `/agent/${agent.public_id}`
        );
      }*/

      console.log(
        "!!! SETTING SELECTED TRAINING WORKFLOW: ",
        agent.training_workflow_id
      );

      if (agent && agent_id === agent.public_id) {
        setLoading(false);
        setLocalAgent(agent);
      }
    }

    // eslint-disable-next-line
  }, [agent]);

  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    console.log("STATE: ", location);
  }, [location]);

  const handleChange = ({ target }) => {
    const a = { ...localAgent };
    a[target.name] = target.value;
    setLocalAgent(a);
  };

  const handleTrainingWorkflowChange = (trainingWorkflow) => {
    const a = { ...localAgent };
    a.training_workflow_id = trainingWorkflow.value;
    a.training_workflow = trainingWorkflows.find(
      (w) => w.id === trainingWorkflow.value
    );
    setLocalAgent(a);
    setSelectedTrainingWorkflow(trainingWorkflow);
  };

  const handleInferenceWorkflowChange = (inferenceWorkflow) => {
    const a = { ...localAgent };
    a.inference_workflow_id = inferenceWorkflow.value;
    setLocalAgent(a);
    setSelectedInferenceWorkflow(inferenceWorkflow);
  };

  const validateFields = () => {
    setFormErrors({});
    const errors = {};

    console.log("agent: ", localAgent);
    const nameValid = localAgent.name && localAgent.name.length > 0;

    errors.name = nameValid ? "" : " is required";

    console.log("errors: ", errors);
    setFormErrors(errors);

    return nameValid;
  };

  const save = () => {
    console.log("save agent: ", localAgent);
    if (validateFields()) {
      dispatch(saveAgent(localAgent));
    }
  };

  const showMedia = (media) => {
    /*const media = {};
    mediaHead.forEach((key, index) => {
      media[key] = row[index];
    });*/
    navigate(`/agent/media/${media.public_id}`);
  };

  const showSupportingContent = (row) => {
    const supportingContent = {};
    supportingContentHead.forEach((key, index) => {
      supportingContent[key] = row[index];
    });
    navigate(`/supportingcontent/${supportingContent.public_id}`);
  };

  const showChatSession = (row) => {
    const chat_session = {};
    chatSessionsHead.forEach((key, index) => {
      chat_session[key] = row[index];
    });
    const chat_session_id = chat_session.public_id;
    console.log(
      "SHOW MODAL: ",
      localAgent.id,
      localAgent.name,
      chat_session_id
    );
    NiceModal.show("chat-session", { agent: localAgent, chat_session_id });
  };

  if (loading) {
    return (
      <>
        <Sidebar {...sidebar} />
        <div
          className="relative md:ml-64 bg-blueGray-100 pt-24"
          style={{ height: "100%", minHeight: "100vh" }}
        >
          <div style={{ marginLeft: "50px" }}>Loading Agent...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar {...sidebar} navigate={navigate} />
      <div
        className="relative md:ml-64 bg-blueGray-100 pt-24"
        style={{ height: "100%", minHeight: "100vh" }}
      >
        <div className="px-4 md:px-6 mx-auto w-full -mt-24 pt-24">
          <div className="flex flex-wrap">
            <div className="relative p-6 flex-auto ">
              <div>
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    navigate("/admin/agents");
                  }}
                >
                  <i className="fa fa-arrow-left" /> Back to Agents
                </div>{" "}
              </div>
              <h3 className="font-bold text-lg text-blueGray-700">
                {localAgent?.name ?? ""}
              </h3>
              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <label>ID</label>
                <Input
                  label="ID"
                  placeholder="ID"
                  name="id"
                  value={localAgent && localAgent.id ? localAgent.id : ""}
                  disabled
                />
                <label>Public ID</label>
                <Input
                  label="Public ID"
                  placeholder="Public ID"
                  name="public_id"
                  value={
                    localAgent && localAgent.public_id
                      ? localAgent.public_id
                      : ""
                  }
                  disabled
                />
                <label>
                  Name <ErrorField>{formErrors.name}</ErrorField>
                </label>
                <Input
                  label="Name"
                  placeholder="Name"
                  name="name"
                  value={localAgent && localAgent.name ? localAgent.name : ""}
                  onChange={handleChange}
                />
                <label>
                  Description <ErrorField>{formErrors.description}</ErrorField>
                </label>
                <Input
                  type="textarea"
                  label="Description"
                  placeholder="Description"
                  name="description"
                  value={
                    localAgent && localAgent.description
                      ? localAgent.description
                      : ""
                  }
                  onChange={handleChange}
                />
                <label>
                  Training Workflow{" "}
                  <ErrorField>{formErrors.training_workflow}</ErrorField>
                </label>
                <Select
                  label="Training Workflow"
                  placeholder="Choose a Training Workflow"
                  name="training_workflow"
                  value={selectedTrainingWorkflow}
                  options={trainingWorkflowOptions}
                  onChange={handleTrainingWorkflowChange}
                />
                <label>
                  Inference Workflow{" "}
                  <ErrorField>{formErrors.inference_workflow}</ErrorField>
                </label>
                <Select
                  label="Inference Workflow"
                  placeholder="Choose a Inference Workflow"
                  name="inference_workflow"
                  value={selectedInferenceWorkflow}
                  options={inferenceWorkflowOptions}
                  onChange={handleInferenceWorkflowChange}
                />
                <label>
                  Introduction{" "}
                  <ErrorField>{formErrors.introduction}</ErrorField>
                </label>
                <Input
                  type="textarea"
                  label="Introduction"
                  placeholder="Introduction"
                  name="introduction"
                  value={
                    localAgent && localAgent.introduction
                      ? localAgent.introduction
                      : ""
                  }
                  style={{ height: "200px" }}
                  onChange={handleChange}
                />
                <label>
                  Identity <ErrorField>{formErrors.identity}</ErrorField>
                </label>
                <Input
                  type="textarea"
                  label="Identity"
                  placeholder="Identity"
                  name="identity"
                  value={
                    localAgent && localAgent.identity ? localAgent.identity : ""
                  }
                  style={{ height: "200px" }}
                  onChange={handleChange}
                />
                <label>
                  Voice <ErrorField>{formErrors.voice}</ErrorField>
                </label>
                <Input
                  type="textarea"
                  label="Voice"
                  placeholder="Voice"
                  name="voice"
                  value={localAgent && localAgent.voice ? localAgent.voice : ""}
                  style={{ height: "200px" }}
                  onChange={handleChange}
                />
              </div>
              {activeUser && activeUser.user_type === "admin" && (
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={save}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin"></i>{" "}
                        Saving...
                      </>
                    ) : (
                      <>Save Changes</>
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className="w-full px-4">
              <CardFullTable
                title="Chats"
                startClosed={true}
                titlebutton={
                  <i
                    onClick={(e) => {
                      console.log("AGENT NAME: ", localAgent.name);
                      NiceModal.show("chat-session", { agent: localAgent });
                    }}
                    className="fa fa-plus text-sm cursor-pointer"
                  />
                }
                head={chatSessionsHead}
                body={chatSessions}
                onRowClick={(row) => {
                  showChatSession(row);
                }}
                loading={loadingChatSessions}
              />
            </div>
            <div className="w-full px-4">
              <CardFullTable
                title={
                  tokenCount
                    ? `Media (${Intl.NumberFormat().format(tokenCount)} tokens)`
                    : "Media"
                }
                startClosed={true}
                titlebutton={
                  <i
                    onClick={() =>
                      NiceModal.show("add-media", {
                        agent_id,
                        onSaveMedia: (m) => {
                          console.log("SAVE MEDIA: ", m);
                          const newMedia = [...media];
                          newMedia.push(m);
                          setMedia(newMedia);
                        },
                        onError: (message) => {
                          toast.current.show({
                            severity: "error",
                            summary: "Error",
                            detail: message,
                            life: 3000,
                          });
                        },
                      })
                    }
                    className="fa fa-plus text-sm cursor-pointer"
                  />
                }
                body={media}
                renderItem={(item, index) => (
                  <MediaItem
                    item={item}
                    index={index}
                    toast={toast}
                    onRowClick={(item) => showMedia(item)}
                  />
                )}
                loading={loadingMedia}
              />
            </div>
            <div className="w-full px-4">
              <CardFullTable
                title="Supporing Content"
                startClosed={true}
                titlebutton={
                  <i
                    onClick={() =>
                      NiceModal.show("add-supporting-content", {
                        agent,
                      })
                    }
                    className="fa fa-plus text-sm cursor-pointer"
                  />
                }
                head={supportingContentHead}
                body={supportingContent}
                onRowClick={(row) => showSupportingContent(row)}
                loading={loadingSupportingContent}
              />
            </div>
            <Toast ref={toast} />
            <ConfirmDialog />
          </div>
        </div>
      </div>
    </>
  );
};

export default Agent;
